import { useEffect, useState } from 'react';
import { coreAPI } from '../../lib/api';
import { leadTimesAPI, type LeadTimeCommodity, type SupplierLeadTime, type SupplierLeadTimePayload } from '../../lib/api/leadTimes';
import { getErrorMessage } from '../../hooks/useFetchData';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface LeadTimeDialogProps {
  open: boolean;
  onClose: () => void;
  row?: SupplierLeadTime | null;
  onSaved: (row: SupplierLeadTime) => void;
}

interface Option { value: string; label: string }

interface FormState {
  supplier: string;
  commodity_code: string;
  lead_time_days: string;
  effective_from: string;
  effective_to: string;
  notes: string;
}

const DEFAULT_CODE = '__default__';

function initialState(row?: SupplierLeadTime | null): FormState {
  return {
    supplier: row ? String(row.supplier) : '',
    commodity_code: row ? (row.commodity_code || DEFAULT_CODE) : DEFAULT_CODE,
    lead_time_days: row ? String(row.lead_time_days) : '',
    effective_from: row?.effective_from ?? new Date().toISOString().slice(0, 10),
    effective_to: row?.effective_to ?? '',
    notes: row?.notes ?? '',
  };
}

/** Add or change one agreed lead time. Customer users only. */
export default function LeadTimeDialog({ open, onClose, row, onSaved }: LeadTimeDialogProps) {
  const [form, setForm] = useState<FormState>(() => initialState(row));
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<Option[]>([]);
  const [commodities, setCommodities] = useState<Option[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { if (open) setForm(initialState(row)); }, [open, row]);

  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    Promise.all([coreAPI.getContractPricingCompanies(), leadTimesAPI.commodities()])
      .then(([companies, codes]) => {
        if (cancelled) return;
        setSuppliers((companies.data.results || []).filter((c) => c.company_type === 'supplier')
          .sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: String(c.id), label: c.name })));
        setCommodities([
          { value: DEFAULT_CODE, label: 'All commodities (supplier default)' },
          ...(codes.data as LeadTimeCommodity[]).map((c) => ({ value: c.code, label: `${c.code} — ${c.name}` })),
        ]);
        setLoaded(true);
      })
      .catch((err: unknown) => console.error('Lead time options failed:', err));
    return () => { cancelled = true; };
  }, [open, loaded]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.supplier) { toast.error('Pick the supplier.'); return; }
    const days = Number(form.lead_time_days);
    if (!Number.isInteger(days) || days < 1) { toast.error('Lead time is a whole number of days, at least 1.'); return; }
    if (!form.effective_from) { toast.error('Pick the date the lead time applies from.'); return; }
    const payload: SupplierLeadTimePayload = {
      supplier: Number(form.supplier),
      commodity_code: form.commodity_code === DEFAULT_CODE ? '' : form.commodity_code,
      lead_time_days: days,
      effective_from: form.effective_from,
      effective_to: form.effective_to || null,
      notes: form.notes,
    };
    setSaving(true);
    try {
      const res = row ? await leadTimesAPI.update(row.id, payload) : await leadTimesAPI.create(payload);
      toast.success(row ? 'Lead time updated' : 'Lead time added');
      onSaved(res.data);
      onClose();
    } catch (err: unknown) {
      console.error('Lead time save failed:', err);
      toast.error(getErrorMessage(err, 'Could not save the lead time'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="lead-time-dialog">
        <DialogHeader><DialogTitle>{row ? 'Change lead time' : 'Add a lead time'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Supplier</Label>
            {row ? (
              <p className="text-sm text-foreground">{row.supplier_name}</p>
            ) : (
              <SearchableSelect options={suppliers} value={form.supplier} onChange={(v) => set('supplier', v)}
                                placeholder={loaded ? 'All suppliers — pick one' : 'Loading suppliers…'} />
            )}
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Commodity</Label>
            <SearchableSelect options={commodities} value={form.commodity_code} onChange={(v) => set('commodity_code', v)} placeholder="All commodities (supplier default)" />
            <p className="text-xs text-muted-foreground">A commodity-specific lead time wins over the supplier default when the monthly file carries a Commodity_Code.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lt-days">Lead time (days)</Label>
            <Input id="lt-days" type="number" min={1} max={365} value={form.lead_time_days} onChange={(e) => set('lead_time_days', e.target.value)} data-testid="lead-time-days" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lt-from">Applies from</Label>
            <Input id="lt-from" type="date" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} data-testid="lead-time-from" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lt-to">Applies until</Label>
            <Input id="lt-to" type="date" value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} />
            <p className="text-xs text-muted-foreground">Leave blank for open-ended.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lt-notes">Notes</Label>
            <Input id="lt-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="e.g. per framework schedule 3" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="lead-time-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {row ? 'Save changes' : 'Add lead time'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
